import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { ThemeProvider as StyledProvider } from 'styled-components';
import { ThemeProvider as MuiProvider } from '@material-ui/core/styles';
// v5 pre-flight (ECOMM-10311 Phase 1, tracker #20948): no @mui/material components render yet,
// so this is inert scaffolding. `injectFirst` makes Emotion (v5's CSS engine) inject its <style>
// tags before styled-components' in <head>, so styled-components can always win specificity ties
// once Phases 3-6 start rendering real @mui/* components here. Marketplace has no v4 JSS
// StylesProvider equivalent currently wired (Phase 0 removed all makeStyles usage, and v4's own
// built-in component styles inject via the default JSS ordering), so there's no existing sibling
// provider to match — this is the first and only style-engine-order provider in this tree until
// Phase 7 drops v4. Remove once v4 is fully gone.
import { StyledEngineProvider } from '@mui/material/styles';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getStyledTheme } from 'src/theme/builders/styled';
import { getMuiTheme } from 'src/theme/builders/mui';
import { getColors, isColorDark, shadows, fonts as baseFonts, buildScale } from 'src/theme';
import { ProductCardButtonOption, RadiusOption, TextTransformOption } from 'src/gql/graphql';
import { ColorsProvider } from 'contexts/colors';
import useUI from 'hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import PublicEnv from 'shared/utils/public-env';
import themes from 'theme/constants/demo';

import { CuraleafFonts } from 'shared/utils/customize';
import { defaultTagColors } from 'shared/constants/menu-colors';
import { mergeWithoutNull } from 'src/utils/misc-utils';

import { GlobalSliderStyle } from 'components/core/range-slider/material-ui-slider';
import { RedesignVariables } from 'src/home/redesign/redesign-variables';

import { GlobalFontStyles } from '../constants/global-styles';
import { CoreMenuVariables } from '../core-menu-variables';
import { WebsiteThemeVariables } from '../website-theme-variables';
import { CommerceComponentsContext } from './commerce-components/context';
import { CommerceBridgeProvider } from './commerce-components/bridge/bridge-context';
import { useRemoteCommerceComponents } from './commerce-components/remote-theme';
import { useMetaFields } from './commerce-components/use-meta-fields';

import {
  getEncodedFonts,
  getFullFontFamily,
  getRadiusStyle,
  getTextTransformStyle,
  getWebCustomizationSettings,
} from '../theme.utils';

// Runs useMetaFields from *inside* CommerceBridgeProvider. useMetaFields reads the commerce bridge
// via context, and the provider is rendered by ThemeProvider itself, so calling the hook directly in
// ThemeProvider would always see the context default (null) and never invoke the theme's
// getStoreFrontMetaFields. This child publishes the result back up into ThemeProvider state
// (the same publish pattern CommerceBridgeDataSource uses).
const getCurrentPathname = () => (typeof window !== 'undefined' ? window.location.pathname : '');

function MetaFieldsSource({ skipExtraction, themeIdentity, publish }) {
  const fields = useMetaFields(skipExtraction, themeIdentity);
  const pathname = getCurrentPathname();

  useEffect(() => {
    // Tag the value with the route it was resolved for so ThemeProvider can drop it the moment
    // the route changes, instead of exposing it until this effect re-publishes.
    publish({ pathname, themeIdentity, fields });
  }, [fields, pathname, themeIdentity, publish]);

  useEffect(
    () => () => {
      publish(null);
    },
    [publish]
  );

  return null;
}

export function ThemeProvider(props) {
  const { children, deviceType = 'desktop' } = props;
  const cart = useCart();
  const useDispensaryHook = useDispensary();
  const UI = useUI();

  const flags = useFlags();
  const isCuraleafFontsEnabled = flags['growth.ecomm.curaleaf-fonts.entitlement'] ?? false;
  // Single shared call to the remote theme's getStoreFrontMetaFields function. Computed once
  // here (rather than separately in Header, CanonicalTag, etc.) so the merchant theme's function
  // only runs once per navigation, and downstream consumers agree on the same value.
  const derivedMetaFieldsEnabled = flags['growth.ecomm.get-store-front-meta-fields-function.rollout'] ?? false;
  const [publishedMetaFields, setFunctionMetaFields] = useState(null);

  let {
    query: { cName },
  } = useRouter();

  const router = useRouter();
  const isEmbedded = useObserver(() => UI.isEmbedded);
  const isDutchieMain = useObserver(() => UI.isDutchieMain);
  const isInstoreEnrollmentRoute = router?.pathname.includes('/instore-enrollment');
  const isEnterpriseEnrollmentRoute = router?.pathname.includes('/enterprise/');
  const isEnterpriseSignupRoute = isEnterpriseEnrollmentRoute && router?.pathname.endsWith('/signup');
  const isCmsRoute = router?.pathname.startsWith('/cms/');
  const isKiosk = useObserver(() => UI.isKiosk);
  const isMobileEcommApp = useObserver(() => UI.isMobileEcommApp);
  const dispensaryFromCart = useObserver(() => cart.dispensary);
  const { dispensary: currentDispensary } = useCurrentDispensary();
  const dispensaryFromHook = useObserver(() => useDispensaryHook.dispensary);
  const dispensaryFromEnterprise = useObserver(() => UI.enterpriseDispensary);
  const dispensary = dispensaryFromCart || currentDispensary || dispensaryFromHook || dispensaryFromEnterprise;
  const websiteTheme = useObserver(() => UI.websiteTheme);

  const encodedFontsRef = useRef();

  const { getCommerceComponents, shouldProvideCommerceBridge } = useRemoteCommerceComponents();

  if (!cName && dispensary) {
    cName = dispensary.cName;
  }

  const shouldUseDemoSkins = PublicEnv.appEnv === 'demo' || PublicEnv.appEnv === 'development';

  // memoize the themes so they don't get regenerated
  const stores = useMemo(() => {
    // Embedded menus wait for a dispensary, but enterprise signup must render its fallback
    // theme before the optional branding dispensary resolves.
    if (!shouldUseDemoSkins && !dispensary && isEmbedded && !isEnterpriseSignupRoute) {
      return null;
    }

    if (!dispensary && isKiosk) {
      // wait for dispensary in kiosk mode too, we need this for high performance mode to *always* work
      return null;
    }

    let themecName = cName;

    if (!shouldUseDemoSkins || !isEmbedded || !isMobileEcommApp || !themecName) {
      themecName = `default`;
    }

    const theme = themes[themecName] || themes.default;
    const colors = { ...getColors(), ...theme.colors };

    let customized = {
      colors: {
        buttonsLinks: isDutchieMain ? `#4d2a3d` : colors.primaryBlue,
        navBar: colors.primaryGrey,
        staffPickTag: defaultTagColors.staffPick,
        discountTag: defaultTagColors.discount,
        productCardButton: ProductCardButtonOption.tinted,
        strainTypes: undefined,
      },
      fonts: {
        primary: baseFonts.join(', '),
        secondary: baseFonts.join(', '),
      },
      textTransforms: {
        buttons: getTextTransformStyle(TextTransformOption.upper),
        links: getTextTransformStyle(TextTransformOption.title),
        tags: getTextTransformStyle(TextTransformOption.title),
      },
      radius: getRadiusStyle(RadiusOption.rounded),
      utils: {
        isNavBarDark: false,
      },
      components: {
        storeFront: {
          header: null,
          footer: null,
        },
      },
      maxSiteWidth: 1200,
    };

    // Fonts actually requested from Google Fonts via the `<link>` below -- NOT the same
    // question as which font `--dcm-font-*`/`theme.customized.fonts.*` resolves to for a given
    // surface. Collected into an array (not an object merge) specifically so a Website Theme
    // font ADDS a webfont request rather than replacing the dispensary's own: several surfaces
    // still read `theme.customized.fonts.*`/`--dct--customized-fonts-*` directly instead of the
    // `--dcm-font-*` vars `WebsiteThemeVariables` overrides (e.g. `components/cart/item.jsx`,
    // `components/menu-header/menu-header.jsx`) and keep rendering the dispensary font on those
    // surfaces even once a Website Theme font is set -- an object-merge-based replace would
    // stop that font's bytes from ever being requested, breaking it everywhere it's still used.
    let dispensaryFontValues = [];

    if (!isDutchieMain || isInstoreEnrollmentRoute || isEnterpriseEnrollmentRoute || isCmsRoute) {
      const { colors: customColors, fonts: customFonts, textTransforms, radius } = getWebCustomizationSettings(
        dispensary
      );

      const fullFamilies = {
        primary: getFullFontFamily(customFonts.primary),
        secondary: getFullFontFamily(customFonts.secondary),
      };

      customized = mergeWithoutNull(customized, {
        colors: customColors,
        fonts: fullFamilies,
        textTransforms,
        radius,
        utils: {
          isNavBarDark: isColorDark(customColors.navBar),
        },
      });

      dispensaryFontValues = Object.values(customFonts);
    }

    // Computed OUTSIDE the branch above (unlike dispensaryFontValues) because `websiteTheme` can
    // be in scope on a route shape that branch's condition doesn't cover -- e.g. an enterprise
    // custom domain's homepage, which resolves `isDutchieMain: true` with none of
    // isInstoreEnrollmentRoute/isEnterpriseEnrollmentRoute/isCmsRoute true either, since its
    // Next.js route is the plain `/` pattern (see `UIStore.isEnterpriseCmsRoot`/`isStoreFront`),
    // yet still needs its Website Theme font's webfont bytes requested. `getEncodedFonts` already
    // dedupes internally, so unioning both sources here is safe even when they overlap.
    encodedFontsRef.current = getEncodedFonts([...dispensaryFontValues, ...Object.values(websiteTheme?.fonts ?? {})]);

    const embeddedMenu = { height: theme.banner?.height || 0 };
    const muiTheme = getMuiTheme({ colors, customized, deviceType });
    const styledTheme = getStyledTheme({
      colors,
      embeddedMenu,
      shadows,
      breakpoints: muiTheme.breakpoints,
      spaces: buildScale(4, 20),
      customized,
    });

    return {
      theme,
      colors,
      styledTheme,
      muiTheme,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cName,
    isEmbedded,
    dispensary,
    isDutchieMain,
    isInstoreEnrollmentRoute,
    isEnterpriseEnrollmentRoute,
    isEnterpriseSignupRoute,
    isCmsRoute,
    deviceType,
    websiteTheme,
  ]);

  // `commerceComponents` intentionally does NOT depend on `metaFields`. `metaFields` changes on
  // every client-side navigation (it's re-derived per-pathname in useMetaFields), while
  // `getCommerceComponents(...)` rebuilds a brand-new set of wrapped remote component factories
  // (including a fresh GlobalStyles factory) every time it's invoked. If metaFields were a dep
  // here, every navigation would call getCommerceComponents() again, producing a new GlobalStyles
  // component identity below and forcing RemoteGlobalStyles to unmount/remount - tearing down and
  // re-injecting its global CSS on every nav. Keeping metaFields out of this memo's deps means
  // getCommerceComponents() (and therefore component identities) only change when the remote
  // theme/module itself actually changes.
  const commerceComponents = useMemo(() => getCommerceComponents(stores?.styledTheme), [
    stores?.styledTheme,
    getCommerceComponents,
  ]);

  // Only expose fields that were resolved for the current route AND the active theme; a published value from the
  // previous route or theme must never reach CanonicalTag/Header while the new route's request is pending.
  const metaFields =
    publishedMetaFields &&
    publishedMetaFields.pathname === getCurrentPathname() &&
    publishedMetaFields.themeIdentity === commerceComponents.themeIdentity
      ? publishedMetaFields.fields
      : null;

  // Depends only on the stable `commerceComponents`, not on the metaFields-carrying context value,
  // so this component's identity (and thus its mount lifecycle) is unaffected by metaFields churn.
  const RemoteGlobalStyles = useMemo(() => commerceComponents.components.GlobalStyles({}), [commerceComponents]);

  // The context value merges the stable `commerceComponents` with the frequently-changing
  // `metaFields`. This object's identity does change on every metaFields update (as intended -
  // CanonicalTag/Header need to re-render with the new value), but nothing above (notably
  // RemoteGlobalStyles) depends on *this* memo, so that churn no longer cascades into remounts.
  const commerceComponentsTheme = useMemo(
    () => ({
      ...commerceComponents,
      metaFields,
    }),
    [commerceComponents, metaFields]
  );

  if (!stores) {
    return null;
  }

  return (
    <>
      <ColorsProvider value={stores.colors}>
        <StyledProvider theme={stores.styledTheme}>
          <CommerceBridgeProvider enabled={shouldProvideCommerceBridge} resetKeys={[dispensary?.id, router?.asPath]}>
            <MetaFieldsSource
              skipExtraction={!derivedMetaFieldsEnabled}
              themeIdentity={commerceComponents.themeIdentity}
              publish={setFunctionMetaFields}
            />
            <GlobalFontStyles />
            {/* Base styles for the forked MUI Slider (components/core/range-slider). Mounted once here
              rather than per Slider instance. Given the MUI theme explicitly because createGlobalStyle
              otherwise reads the styled-components theme from context, and its interpolations expect
              the MUI theme shape (palette/typography/transitions). */}
            <GlobalSliderStyle theme={stores.muiTheme} />
            <CoreMenuVariables $isPlum={isDutchieMain} />
            {isDutchieMain && <RedesignVariables />}
            <RemoteGlobalStyles />
            {/* Must render AFTER CoreMenuVariables/RemoteGlobalStyles (DOM/mount order, not specificity,
              is what gives this precedence over both -- see PLAN.md's locked cascade order and
              WebsiteThemeVariables' own docblock). `websiteTheme` is bridged in from whichever
              enterprise-domain page content resolved a saved website (useWebsite) or the CMS editor's
              in-progress draft (ThemeDraftProvider) -- see UI.websiteTheme's own doc comment. */}
            <WebsiteThemeVariables theme={websiteTheme} />
            {!isDutchieMain && isCuraleafFontsEnabled && <CuraleafFonts />}

            {/* No separate isDutchieMain/isCmsRoute gate here -- `encodedFontsRef.current` above
              already reflects, route-aware, whether there's anything to load: it's recomputed
              (not left stale) on every change to isDutchieMain/isInstoreEnrollmentRoute/etc.,
              since those are all in the `stores` useMemo's own deps array above. An
              isDutchieMain instore-enrollment/enterprise-enrollment route now DOES request the
              dispensary's Google Fonts here, where the old gate suppressed them -- an
              intentional fix, not a regression: `customized.fonts` (built from the same
              `customFonts` a few lines up) already references those fonts on exactly those
              routes, so the webfont bytes need requesting there too. */}
            {encodedFontsRef.current && (
              <>
                <link rel='preconnect' href='https://fonts.googleapis.com' />
                <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
                <link
                  id='customFont'
                  rel='stylesheet'
                  href={`https://fonts.googleapis.com/css2?${encodedFontsRef.current}&display=swap`}
                />
              </>
            )}
            <StyledEngineProvider injectFirst>
              <MuiProvider theme={stores.muiTheme}>
                <CommerceComponentsContext.Provider value={commerceComponentsTheme}>
                  {children}
                </CommerceComponentsContext.Provider>
              </MuiProvider>
            </StyledEngineProvider>
          </CommerceBridgeProvider>
        </StyledProvider>
      </ColorsProvider>
    </>
  );
}
